<template>
  <chart :option="calOptions" ref="chart" class="chart-cont" :style="{ height: chartHeight + 'px' }"></chart>
</template>

<script>
import lodash, { merge } from 'lodash';
import Chart from '@/components/echarts/chart.vue';
import { getSankeyChartTooltip } from './sankey-chart-tooltip.vue';
import { optionGen } from '@/utils/helper';

const numeral = require('numeral');

const COLORS = [
  '#338BFF',
  '#29CC85',
  '#FADA66',
  '#FF7656',
  '#4C6293',
  '#5AD5E0',
  '#AD4EC7',
  '#0752C9',
  '#00A169',
  '#F1A927',
  '#DA4724',
  '#254374',
  '#00AEBE',
  '#882EB0',
  '#87C1FF',
  '#71EC9E',
  '#FFF59A',
  '#FFAE91',
  '#7888BA',
  '#ACF2F9',
  '#D77DF1',
  '#022277',
  '#007649',
  '#D17600',
  '#AA2300',
  '#00204F',
  '#008293',
  '#5E0E99',
];

export default {
  name: 'SankeyChart',
  components: {
    Chart,
  },
  props: {
    isStart: {
      type: Boolean,
      default: () => {
        return true;
      },
    },
    sankeyData: {
      type: Object,
      default: () => {
        return {};
      },
    },
  },
  data() {
    return {
      observer: null,
      oldChartHeight: 0,
      nodes: [],
      links: [],

      cacheClickParams: null,
    };
  },
  computed: {
    chartHeight() {
      const { nodes } = this.sankeyData;
      if (!nodes) {
        return 460;
      }
      let maxDepthNodeNum = 0;
      Object.keys(nodes).forEach(key => {
        maxDepthNodeNum = nodes[key].length > maxDepthNodeNum ? nodes[key].length : maxDepthNodeNum;
      });
      return `${120 * maxDepthNodeNum}`;
    },
    calOptions() {
      const options = optionGen();

      merge(options, {
        tooltip: [
          {
            trigger: 'item',
            renderMode: 'html',
            className: 'sankey-tooltip',
            borderWidth: 0,
            enterable: true,
            formatter: this.getDefaultFormatter(),
            position: ([x, y], params, dom) => {
              return [x - dom.clientWidth / 2, y - dom.clientHeight - 30];
            },
          },
        ],
        xAxis: { show: false },
        series: [
          {
            type: 'sankey',
            left: '20px',
            top: '10px',
            right: '138px',
            bottom: '10px',
            nodeWidth: 22,
            nodeGap: 35,
            layoutIterations: 0,
            draggable: false,
            data: this.nodes,
            links: this.links,
            emphasis: {
              disabled: true,
            },
            lineStyle: {
              opacity: 0.6,
              curveness: 0.5,
            },
            label: {
              formatter: params => {
                let label = this.getShowLabel(params.data.eventInfo.showLabel) + params.data.eventInfo.name;
                if (params.data.eventInfo.groupValue) {
                  label += `\n${params.data.eventInfo.groupValue}`;
                }
                return params.data.value ? label : '';
              },
            },
          },
        ],
      });

      return options;
    },
  },
  watch: {
    sankeyData: {
      handler() {
        this.translateSankeyData();
      },
      deep: true,
    },
  },
  mounted() {
    this.translateSankeyData();

    const sankeyChart = this.$refs.chart.$refs.echart.chart;

    // 监听容器高度变化
    const chartEle = this.$refs.chart.$el;
    const MutationObserver = window.MutationObserver || window.WebKitMutationObserver || window.MozMutationObserver;
    this.observer = new MutationObserver(() => {
      const height = getComputedStyle(chartEle).getPropertyValue('height');
      if (height !== this.oldChartHeight) {
        this.oldChartHeight = height;
        sankeyChart.resize({
          height: this.chartHeight,
        });
      }
    });
    this.observer.observe(chartEle, { attributes: true, attributeFilter: ['style'], attributeOldValue: true });

    // 监听点击图表
    sankeyChart.on('click', 'series', params => {
      const currOption = sankeyChart.getOption();

      // 点击连线或置灰的节点
      if (
        params.dataType === 'edge' ||
        (params.dataType === 'node' && params.data.itemStyle.opacity && params.data.itemStyle.opacity === 0.1) ||
        (params.dataType === 'node' && params.data.value === 0)
      ) {
        this.cacheClickParams = null;
        currOption.tooltip[0].alwaysShowContent = !!this.cacheClickParams;
        currOption.tooltip[0].formatter = this.getDefaultFormatter();
        currOption.tooltip[0].position = ([x, y], p, dom) => {
          return [x - dom.clientWidth / 2, y - dom.clientHeight - 30];
        };
        sankeyChart.setOption(currOption);
        return;
      }
      //
      this.cacheClickParams = this.cacheClickParams ? null : params;
      const formatter = this.cacheClickParams ? this.getOperationFormatter() : this.getDefaultFormatter();
      currOption.tooltip[0].alwaysShowContent = !!this.cacheClickParams;
      currOption.tooltip[0].formatter = formatter;
      currOption.tooltip[0].position = ([x, y], p, dom) => {
        return this.cacheClickParams
          ? [
              this.cacheClickParams.event.offsetX - dom.clientWidth / 2,
              this.cacheClickParams.event.offsetY - dom.clientHeight - 30,
            ]
          : [x - dom.clientWidth / 2, y - dom.clientHeight - 30];
      };
      sankeyChart.setOption(currOption);
    });
  },
  methods: {
    translateSankeyData() {
      const sankeyData = lodash.cloneDeep(this.sankeyData);
      const eventMap = sankeyData.events;
      let edgeArr = sankeyData.pathData;
      let depthDotMap = sankeyData.nodes;

      if (
        !eventMap ||
        !Object.keys(eventMap).length ||
        !edgeArr ||
        !edgeArr.length ||
        !depthDotMap ||
        !Object.keys(depthDotMap).length
      ) {
        return;
      }

      if (!this.isStart) {
        depthDotMap = this.reverseDepthDotMap(depthDotMap);
        edgeArr = this.reverseEdgeArr(depthDotMap, edgeArr);
      }

      let nodes = [];
      const links = [];
      const linkMap = {};
      const allDepth = Object.keys(depthDotMap).length - 1;

      let index = 0;
      Object.keys(eventMap).forEach(key => {
        eventMap[key].color = COLORS[index];
        if (index === COLORS.length - 1) {
          index = 0;
        } else {
          index += 1;
        }
      });

      // eslint-disable-next-line guard-for-in,no-restricted-syntax
      for (let depth in depthDotMap) {
        const dots = depthDotMap[depth];
        depth = parseInt(depth, 10);
        if (dots && dots.length) {
          const subNodes = dots.map(dot => {
            const event = eventMap[dot.showLabel];
            return {
              name: `${depth}-${dot.showLabel}`,
              value: dot.total,
              itemStyle: {
                color: event.color,
              },
              depth,
              dotInfo: dot,
              eventInfo: event,
              isHighLight: false,
            };
          });

          // 补充节点值为0的节点(解决流失用户需要底部对齐的需求)
          if (subNodes.length < 11 && ((this.isStart && depth > 0) || (!this.isStart && depth < allDepth))) {
            Object.keys(eventMap)
              .filter(key => key !== '65535')
              .forEach(key => {
                const exist = subNodes.find(item => `${item.dotInfo.showLabel}` === `${key}`);
                if (!exist) {
                  const event = eventMap[key];
                  subNodes.push({
                    name: `${depth}-${key}`,
                    value: 0,
                    itemStyle: {
                      color: event.color,
                    },
                    depth,
                    dotInfo: { loss: 0, total: 0 },
                    eventInfo: event,
                    isHighLight: false,
                  });
                }
              });
            subNodes.splice(11, subNodes.length - 11);
          }

          subNodes.sort((pre, next) => {
            return next.value - pre.value;
          });

          // 加其他分组节点
          if (subNodes.length >= 11) {
            subNodes.push({
              name: `${depth}-other-group`,
              value: 0,
              itemStyle: {
                color: '#731FA4',
              },
              depth,
              dotInfo: { loss: 0, total: 0 },
              eventInfo: { eventName: 'other_group', groupValue: null, name: '其他分组', showLabel: 'OTHER_GROUP' },
              isHighLight: false,
            });
          }

          // 如果是起始事件，加流失用户节点
          if (this.isStart && depth > 0) {
            const lossVal = depthDotMap[depth - 1].reduce((total, item) => total + item.loss, 0);
            subNodes.push({
              name: `${depth}-65535`,
              value: lossVal,
              itemStyle: {
                color: '#888',
              },
              depth,
              dotInfo: { loss: lossVal, total: lossVal },
              eventInfo: { eventName: 'loss', groupValue: null, name: '流失用户', showLabel: 'LOSS' },
              isHighLight: false,
            });
          }

          nodes = nodes.concat(subNodes);
        }
      }

      edgeArr.forEach(edge => {
        const sourceEvent = eventMap[edge[2]];
        const targetEvent = eventMap[edge[1]];
        let sourceNode = nodes.find(node => node.name === `${edge[0]}-${edge[2]}`);
        let targetNode = nodes.find(node => node.name === `${edge[0] + 1}-${edge[1]}`);
        if (!sourceNode) {
          sourceNode = nodes.find(node => node.name === `${edge[0]}-other-group`);
          if (edge[1] === 65535) {
            sourceNode.dotInfo.loss += edge[3];
          }
        }
        if (!targetNode) {
          targetNode = nodes.find(node => node.name === `${edge[0] + 1}-other-group`);
          targetNode.value += edge[3];
          targetNode.dotInfo.total += edge[3];
        }

        let link = linkMap[sourceNode.name + targetNode.name];
        if (!link) {
          link = {
            source: sourceNode.name,
            target: targetNode.name,
            value: 0,
            sourceDepth: edge[0],
            targetDepth: edge[0] + 1,
            sourceEventInfo: sourceEvent,
            targetEventInfo: targetEvent,
            sourceNode,
            targetNode,
            lineStyle: {
              color: targetEvent.eventName === 'loss' ? 'target' : 'source',
            },
          };
          linkMap[sourceNode.name + targetNode.name] = link;
          links.push(link);
        }

        link.value += edge[3];
      });

      // 如果是起始事件，添加流失用户到流失用户的线
      if (this.isStart) {
        // eslint-disable-next-line no-restricted-syntax,guard-for-in
        for (let depth in depthDotMap) {
          depth = parseInt(depth, 10);
          if (depth > 1) {
            const sourceNode = nodes.find(node => node.name === `${depth - 1}-65535`);
            const targetNode = nodes.find(node => node.name === `${depth}-65535`);
            if (sourceNode && targetNode && sourceNode.value > 0) {
              targetNode.value += sourceNode.value;
              targetNode.dotInfo.loss += sourceNode.value;
              targetNode.dotInfo.total += sourceNode.value;
              links.push({
                source: sourceNode.name,
                target: targetNode.name,
                value: sourceNode.value,
                sourceDepth: depth - 1,
                targetDepth: depth,
                sourceEventInfo: sourceNode.eventInfo,
                targetEventInfo: targetNode.eventInfo,
                sourceNode,
                targetNode,
                lineStyle: {
                  color: 'target',
                },
              });
            }
          }
        }
      }

      this.nodes = nodes;
      this.links = links;
    },
    getDefaultFormatter(showType = 'default') {
      return params => {
        const { dataType, data, value } = params;
        const ele = getSankeyChartTooltip({
          dataType,
          data,
          showType,
          alwaysShowContent: true,
          num: this.getTipsNum(value),
          rate: this.getTipsRate(dataType, data),
        });
        return ele.$el;
      };
    },
    getOperationFormatter() {
      const sankeyChart = this.$refs.chart.$refs.echart.chart;
      return () => {
        if (!this.cacheClickParams) {
          return null;
        }
        const { dataType, data, value, name } = this.cacheClickParams;
        const ele = getSankeyChartTooltip({
          showType: 'operation',
          dataType,
          data,
          num: this.getTipsNum(value),
          rate: this.getTipsRate(dataType, data),
          highLightNodeBack: isHighLight => {
            const currOption = sankeyChart.getOption();
            const currNodes = currOption.series[0].data;
            const currLinks = currOption.series[0].links;

            const currNode = currNodes.find(item => item.name === name);
            currNode.isHighLight = isHighLight;

            // 高亮当前节点（其他层级高亮节点保留）
            if (isHighLight) {
              const allHighLightNodes = currNodes.filter(item => item.isHighLight);
              const allNeedHighLightNodeNames = [];
              const allNeedHighLightLinkNames = [];

              allHighLightNodes.forEach(highLightNode => {
                currLinks.forEach(link => {
                  link.lineStyle = { ...link.lineStyle, opacity: 0.1 };
                });
                currNodes.forEach(node => {
                  node.itemStyle = { ...node.itemStyle, opacity: 0.1 };
                });
                this.setHighLightNodeAndLinks([highLightNode], true, true, currLinks, currNodes);
                this.setHighLightNodeAndLinks([highLightNode], true, false, currLinks, currNodes);

                allNeedHighLightNodeNames.push(
                  currNodes.filter(node => node.itemStyle.opacity === 1).map(node => node.name),
                );
                allNeedHighLightLinkNames.push(
                  currLinks.filter(link => link.lineStyle.opacity === 0.6).map(link => link.source + link.target),
                );
              });

              // 计算所有高亮节点和连线的交集
              const allNeedHighLightNodes = lodash.intersection(...allNeedHighLightNodeNames);
              const allNeedHighLightLinks = lodash.intersection(...allNeedHighLightLinkNames);
              currLinks.forEach(link => {
                link.lineStyle = {
                  ...link.lineStyle,
                  opacity: allNeedHighLightLinks.includes(link.source + link.target) ? 0.6 : 0.1,
                };
              });
              currNodes.forEach(node => {
                node.itemStyle = { ...node.itemStyle, opacity: allNeedHighLightNodes.includes(node.name) ? 1 : 0.1 };
              });
            }

            // 取消高亮（所有层级高亮全部取消）
            if (!isHighLight) {
              currLinks.forEach(link => {
                link.lineStyle = { ...link.lineStyle, opacity: 0.6 };
              });
              currNodes.forEach(node => {
                node.isHighLight = false;
                node.itemStyle = { ...node.itemStyle, opacity: 1 };
              });
            }

            currOption.tooltip[0].alwaysShowContent = false;
            currOption.tooltip[0].formatter = this.getDefaultFormatter();
            currOption.tooltip[0].position = ([x, y], p, dom) => {
              return [x - dom.clientWidth / 2, y - dom.clientHeight - 30];
            };
            currOption.series[0].data = currNodes;
            currOption.series[0].links = currLinks;
            sankeyChart.setOption(currOption);
            this.cacheClickParams = null;
          },
        });
        return ele.$el;
      };
    },
    getTipsNum(num) {
      return numeral(num).format('0,0');
    },
    getTipsRate(dataType, data) {
      if (dataType === 'edge') {
        return numeral(data.value / data.sourceNode.value).format('0.00%');
      }
      return numeral(data.dotInfo.loss / data.dotInfo.total).format('0.00%');
    },
    getShowLabel(showLabel) {
      if (showLabel === 'UN_SELECTED' || showLabel === 'LOSS' || showLabel === 'OTHER_GROUP') {
        return '';
      }
      return `[${showLabel}]`;
    },
    setHighLightNodeAndLinks(currDepthDatas, isNode, isGetBefore, currLinks, currNodes) {
      if (!currDepthDatas || !currDepthDatas.length) {
        return;
      }
      if (isNode) {
        let currDepthLinks = [];
        currDepthDatas.forEach(node => {
          node.itemStyle = { ...node.itemStyle, opacity: 1 };

          const subCurrDepthLinks = currLinks.filter(link => {
            if (isGetBefore) {
              return link.target === node.name && link.targetDepth === node.depth;
            }
            return link.source === node.name && link.sourceDepth === node.depth;
          });
          currDepthLinks = currDepthLinks.concat(subCurrDepthLinks);
        });
        currDepthLinks = lodash.uniqWith(currDepthLinks, (a, b) => a.source === b.source && a.target === b.target);
        this.setHighLightNodeAndLinks(currDepthLinks, false, isGetBefore, currLinks, currNodes);
      } else {
        let currDepthNodes = [];
        currDepthDatas.forEach(link => {
          link.lineStyle = { ...link.lineStyle, opacity: 0.6 };

          const subCurrDepthNodes = currNodes.filter(node => {
            if (isGetBefore) {
              return node.name === link.source && node.depth === link.sourceDepth;
            }
            return node.name === link.target && node.depth === link.targetDepth;
          });
          currDepthNodes = currDepthNodes.concat(subCurrDepthNodes);
        });
        currDepthNodes = lodash.uniqBy(currDepthNodes, 'name');
        this.setHighLightNodeAndLinks(currDepthNodes, true, isGetBefore, currLinks, currNodes);
      }
    },
    reverseEdgeArr(depthDotMap, edgeArr) {
      const result = [];
      const depth = Object.keys(depthDotMap).length - 2;
      edgeArr.reverse().forEach(([currDepth, target, source, value]) => {
        if (target !== 65535 && source !== 65535) {
          result.push([depth - currDepth, source, target, value]);
        }
      });
      return result;
    },
    reverseDepthDotMap(depthDotMap) {
      const result = {};
      const depth = Object.keys(depthDotMap).length - 1;
      Object.keys(depthDotMap)
        .reverse()
        .forEach(key => {
          result[depth - key] = depthDotMap[key];
        });
      return result;
    },
  },
};
</script>

<style scoped lang="less">
.chart-cont {
  width: 100%;
}
</style>
